// Product offers ("buy 2 get 1 free", "3rd one for $8", "3 for $25"), shared by the shop,
// the cart, checkout and the admin Offers screen.
//
// Every offer repeats: with "buy 2, get 1 free", 3 in the cart = 1 free, 6 = 2 free.
// The saving is stored on each order line (total_price already includes it, plus offer_id,
// offer_label and offer_discount), so coupons, analytics and the WhatsApp messages use the
// offer price without knowing about offers.

export const OFFER_TYPES = [
  { id: 'free', title: 'Buy X, get Y free', example: 'Buy 2, get 1 free' },
  { id: 'price', title: 'Buy X, get Y for a set price', example: 'Buy 2, get the 3rd for $8' },
  { id: 'percent', title: 'Buy X, get Y at % off', example: 'Buy 1, get the 2nd 50% off' },
  { id: 'bundle', title: 'Bundle price', example: '3 for $25' },
];

// Quick starting points in the admin form (prices are filled from the product)
export const OFFER_PRESETS = [
  { id: 'b1g1', label: 'Buy 1, get 1 free', values: { offer_type: 'free', buy_qty: 1, get_qty: 1 } },
  { id: 'b2g1', label: 'Buy 2, get 1 free', values: { offer_type: 'free', buy_qty: 2, get_qty: 1 } },
  { id: 'half2nd', label: '2nd at 50% off', values: { offer_type: 'percent', buy_qty: 1, get_qty: 1, value: 50 } },
  { id: 'third', label: '3rd for a set price', values: { offer_type: 'price', buy_qty: 2, get_qty: 1 } },
  { id: 'bundle3', label: '3 for a set price', values: { offer_type: 'bundle', buy_qty: 3, get_qty: 0 } },
];

export const parsePrice = (price) => {
  if (typeof price === 'number') return Number.isFinite(price) ? price : 0;
  return parseFloat(String(price ?? '').replace(/[^0-9.]/g, '')) || 0;
};

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

// "$8" or "$7.50"
export const formatPrice = (value) => {
  const number = round2(Number(value) || 0);
  return `$${Number.isInteger(number) ? number : number.toFixed(2)}`;
};

const ordinal = (n) => {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'}`;
};

const qty = (value) => Math.max(0, Math.floor(Number(value) || 0));

// Units in one deal: "buy 2 get 1" = 3, "3 for $25" = 3
export const offerGroupSize = (offer) => (offer.offer_type === 'bundle' ? qty(offer.buy_qty) : qty(offer.buy_qty) + qty(offer.get_qty));

export const describeOffer = (offer) => {
  const buy = qty(offer.buy_qty);
  const get = qty(offer.get_qty);
  const value = Number(offer.value) || 0;
  switch (offer.offer_type) {
    case 'free':
      return `Buy ${buy}, get ${get} free`;
    case 'price':
      return get === 1 ? `Buy ${buy}, get the ${ordinal(buy + 1)} for ${formatPrice(value)}` : `Buy ${buy}, get ${get} more for ${formatPrice(value)} each`;
    case 'percent':
      return get === 1 ? `Buy ${buy}, get the ${ordinal(buy + 1)} ${value}% off` : `Buy ${buy}, get ${get} more at ${value}% off`;
    case 'bundle':
      return `${buy} for ${formatPrice(value)}`;
    default:
      return 'Special offer';
  }
};

// How much the offer takes off `quantity` units at `unitPrice`
export const offerDiscount = (offer, unitPrice, quantity) => {
  const price = Math.max(0, Number(unitPrice) || 0);
  const units = qty(quantity);
  const group = offerGroupSize(offer);
  if (!group || !price || units < group) return 0;
  const deals = Math.floor(units / group);
  const get = qty(offer.get_qty);
  const value = Math.max(0, Number(offer.value) || 0);
  let saving = 0;
  if (offer.offer_type === 'free') saving = deals * get * price;
  else if (offer.offer_type === 'price') saving = deals * get * Math.max(0, price - value);
  else if (offer.offer_type === 'percent') saving = deals * get * price * (Math.min(value, 100) / 100);
  else if (offer.offer_type === 'bundle') saving = deals * Math.max(0, group * price - value);
  return round2(saving);
};

export const offerStatus = (offer, now = new Date()) => {
  if (!offer.active) return 'paused';
  if (offer.expires_at && new Date(offer.expires_at) <= now) return 'expired';
  return 'active';
};

export const OFFER_STATUS_LABELS = { active: 'Active', paused: 'Paused', expired: 'Ended' };

export const isOfferLive = (offer, now = new Date()) => offerStatus(offer, now) === 'active';

export const offersForProduct = (offers, productId, now = new Date()) =>
  (offers || []).filter((offer) => String(offer.product_id) === String(productId) && isOfferLive(offer, now));

// How many more units complete the next deal (0 when the quantity fits the deal exactly)
export const unitsToNextDeal = (offer, quantity) => {
  const group = offerGroupSize(offer);
  const remainder = group ? qty(quantity) % group : 0;
  return remainder ? group - remainder : 0;
};

// What the customer is told to add to get the deal, e.g. "Add 1 more and get 1 free"
export const offerNudge = (offer, quantity) => {
  const needed = unitsToNextDeal(offer, quantity);
  if (!needed) return null;
  const get = qty(offer.get_qty);
  const value = Number(offer.value) || 0;
  const add = `Add ${needed} more`;
  switch (offer.offer_type) {
    case 'free': return `${add} and get ${get} free`;
    case 'price': return `${add} and get ${get} for ${formatPrice(value)}${get > 1 ? ' each' : ''}`;
    case 'percent': return `${add} and get ${get} at ${value}% off`;
    case 'bundle': return `${add} to get ${qty(offer.buy_qty)} for ${formatPrice(value)}`;
    default: return null;
  }
};

// Cart lines with offers applied (the best one when a product has several)
export const priceCartLines = (items, offers, now = new Date()) => {
  let fullSubtotal = 0;
  let offerSavings = 0;
  const lines = (items || []).map((item) => {
    const unitPrice = parsePrice(item.price);
    const quantity = qty(item.quantity);
    const fullTotal = round2(unitPrice * quantity);
    const candidates = offersForProduct(offers, item.id, now);
    let best = null;
    candidates.forEach((offer) => {
      const discount = offerDiscount(offer, unitPrice, quantity);
      if (discount > 0 && (!best || discount > best.discount)) best = { offer, discount };
    });
    const offer = best?.offer || candidates[0] || null;
    const discount = best?.discount || 0;
    fullSubtotal += fullTotal;
    offerSavings += discount;
    return {
      item,
      unitPrice,
      quantity,
      fullTotal,
      discount,
      total: round2(fullTotal - discount),
      offer: best ? best.offer : null,
      offerLabel: offer ? describeOffer(offer) : null,
      // Shown when the product has an offer and a few more would unlock (more of) it
      nudge: offer ? offerNudge(offer, quantity) : null,
      addForDeal: offer ? unitsToNextDeal(offer, quantity) : 0,
    };
  });
  fullSubtotal = round2(fullSubtotal);
  offerSavings = round2(offerSavings);
  return { lines, fullSubtotal, offerSavings, subtotal: round2(fullSubtotal - offerSavings) };
};

// Line for orders.order_items
export const toOrderItem = (line) => ({
  id: line.item.id, // product_id
  product_name: line.item.name,
  product_image: line.item.image,
  quantity: line.quantity,
  unit_price: line.unitPrice,
  total_price: line.total,
  ...(line.offer ? { offer_id: line.offer.id, offer_label: describeOffer(line.offer), offer_discount: line.discount } : {}),
});

// Admin preview: what 1, 2, 3 ... units cost with the offer
export const priceByQuantity = (offer, unitPrice, maxQuantity) =>
  Array.from({ length: maxQuantity }, (_, index) => {
    const quantity = index + 1;
    const full = round2(unitPrice * quantity);
    const discount = offerDiscount(offer, unitPrice, quantity);
    return { quantity, full, total: round2(full - discount), discount };
  });

const isWhole = (value, min, max) => Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= max && String(value).trim() !== '';

export const validateOffer = (values, unitPrice) => {
  const errors = {};
  if (!values.product_id) errors.product_id = 'Choose a product';
  const bundle = values.offer_type === 'bundle';
  if (!isWhole(values.buy_qty, bundle ? 2 : 1, 50)) errors.buy_qty = bundle ? 'At least 2' : 'From 1 to 50';
  if (!bundle && !isWhole(values.get_qty, 1, 50)) errors.get_qty = 'From 1 to 50';
  const value = Number(values.value);
  const hasValue = String(values.value ?? '').trim() !== '' && Number.isFinite(value);
  if (values.offer_type === 'price') {
    if (!hasValue || value < 0) errors.value = 'Enter a price';
    else if (unitPrice && value >= unitPrice) errors.value = `Must be less than the normal price (${formatPrice(unitPrice)})`;
  } else if (values.offer_type === 'percent') {
    if (!hasValue || value <= 0 || value > 100) errors.value = 'From 1 to 100';
  } else if (bundle) {
    const normal = unitPrice * (Number(values.buy_qty) || 0);
    if (!hasValue || value <= 0) errors.value = 'Enter the bundle price';
    else if (unitPrice && value >= normal) errors.value = `Must be less than ${formatPrice(normal)} (the normal price)`;
  }
  return errors;
};

// How an offer did, from the orders the dashboard already loaded
export const summarizeOfferOrders = (orders, offerId) => {
  const summary = { orders: 0, units: 0, savings: 0, registered: 0, guests: 0 };
  (orders || []).forEach((order) => {
    if (order.status === 'cancelled' || !Array.isArray(order.order_items)) return;
    const lines = order.order_items.filter((item) => item.offer_id === offerId);
    if (!lines.length) return;
    summary.orders += 1;
    if (order.user_id) summary.registered += 1;
    else summary.guests += 1;
    lines.forEach((item) => {
      summary.units += Number(item.quantity) || 0;
      summary.savings += Number(item.offer_discount) || 0;
    });
  });
  summary.savings = round2(summary.savings);
  return summary;
};
